import { NextPage } from 'next';
import React from 'react';
import {
  Button, ButtonColor, Carousel, FontWeight, IframeWrapper, Logo, Spacing, SpacingName, Typography, TypographyName, Image,
} from '@doveit/lego';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import LandingLayout from '../client/layouts/landing-layout/LandingLayout';
import {
  BackgroundImage, CTA, TextAlign, Video, WrapperSize,
} from '../client/types';
import PageSection from '../client/components/page-section/PageSection';
import * as styles from '../client/templates/join-our-agents/JoinOurAgents.style';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import Hero from '../client/slices/hero/Hero';
import HAccordion from '../client/components/haccordion/HAccordion';
import Markdown from '../client/components/markdown/Markdown';
import { AgentStory as AgentStoryModel, JobPosition } from '../domain/graphcms';
import AgentStory from '../client/components/agent-story/AgentStory';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { AGENT_STORIES } from '../client/graphql/queries/agent';
import { requireFromStaticAsset, requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import FeatureBlock from '../client/blocks/feature-block/FeatureBlock';
import Stripe from '../client/slices/stripe/Stripe';
import { StripeDirection } from '../client/slices/stripe/types';
import TrustBoxMini from '../client/components/trustpilot/trustbox-mini/TrustBoxMini';
import { AppNavigationVariant } from '../client/templates/common/app-navigation/types';
import SaveJobApplication from '../client/containers/save-job-application/SaveJobApplication';
import { JobApplication } from '../services/dtos';
import { createJobApplicationEventLabel, sendJobApplicationEvent } from '../client/services/tracking/jobApplicationTracking';
import {
  BECOME_AGENT_INFO_URL, FRANCHISING_AS, FRANCHISING_URL, JOIN_OUR_AGENTS_INFO_AS, goToPage,
} from '../client/utils/links';
import { JOB_APPLICATION_STORAGE_KEY } from './become-agent';
import { JOB_POSITION_QUERY } from '../client/graphql/queries/job-offer';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import SectionDivider from '../client/components/section-divider/SectionDivider';

export interface JoinOurAgentsPageProps {
  isLanding: boolean,
  jobPosition: JobPosition,
  SEO: PageHeadProps['SEO'],
  hero: {
    title: string,
    description: string,
    cover: BackgroundImage,
    cta: CTA,
  },
  intro?: {
    title: string,
    subtitle: string,
    video: Video,
    description: string,
    cta: CTA,
  },
  advantages: {
    title: string,
    subtitle?: string,
    cta: CTA,
    items: {
      [key: string]: {
        title: string,
        subtitle?: string,
        description?: string,
        image?: {
          title?: string,
          url: string,
          alt: string,
        },
      },
    },
  },
  valuesSection?: {
    title: string,
    subtitle: string,
    items: {
      title: string,
      description: string,
    }[],
    cta: CTA,
  },
  storiesSection?: {
    title: string,
    items: AgentStoryModel[],
    cta: CTA,
  },
  reputationSection: {
    title: string,
    description: string,
    backgroundImage: BackgroundImage,
  },
  franchising?: {
    title: string,
    description: string,
    image: {
      url: string,
      alt: string,
    },
    cta: CTA,
  },
  contact: {
    title: string,
  },
}

export const JoinOurAgentsPage: NextPage<JoinOurAgentsPageProps> = ({
  isLanding,
  jobPosition,
  SEO,
  hero,
  intro,
  valuesSection,
  storiesSection,
  advantages,
  reputationSection,
  franchising,
  contact,
}) => {
  const { current, ref: formRef } = useRefInView({
    threshold: 0,
  });

  const Layout = React.useMemo(() => (isLanding ? LandingLayout : AppLayout), [isLanding]);

  const goToJobApplicationInfoStep = React.useCallback((jobApplication: JobApplication) => {
    const eventLabel = createJobApplicationEventLabel('main', jobPosition, jobApplication.province);
    sendJobApplicationEvent(eventLabel);

    sessionStorage.setItem(JOB_APPLICATION_STORAGE_KEY, JSON.stringify(jobApplication));

    goToPage({
      url: BECOME_AGENT_INFO_URL,
      as: JOIN_OUR_AGENTS_INFO_AS,
    });
  }, [jobPosition]);

  const onFranchisingActionClick = React.useCallback(() => {
    goToPage({
      url: FRANCHISING_URL,
      as: FRANCHISING_AS,
    });
  }, []);

  return (
    <Layout navigationVariant={AppNavigationVariant.LIGHT}>
      <PageHead SEO={SEO} />
      {/** Hero */}
      <Hero
        cover={requireResourcesFromStaticAssets(hero.cover)}
        coverPosition="top left"
      >
        <Typography.H1 color="neutrals.lightest">
          {hero.title}
        </Typography.H1>
        <Spacing margin={[SpacingName.SMALLER, 0, SpacingName.MEDIUM]}>
          <Typography.H3
            color="neutrals.lightest"
            tagName="p"
            fontWeight={FontWeight.LIGHT}
          >
            {hero.description}
          </Typography.H3>
        </Spacing>
        <Button
          {...hero.cta}
          color={ButtonColor.LIGHT}
          onClick={scrollToRef(current)}
        />
      </Hero>

      {/** Intro */}
      {!isLanding && (
        <PageSection
          padding={[SpacingName.BIGGER, 0]}
          aria-label="Intro"
        >
          <PageSection.Wrapper>
            <div css={styles.text}>
              <Typography.H2>
                {intro.title}
              </Typography.H2>
              <Typography.H3
                tagName="p"
                fontWeight={FontWeight.LIGHT}
              >
                {intro.subtitle}
              </Typography.H3>

              <div css={styles.introBlockVideoWrapper}>
                <div css={styles.introSectionVideo}>
                  <IframeWrapper
                    title={intro.video.title}
                    src={intro.video.embedUrl}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    fit
                  />
                </div>
              </div>

              <PageSection.Actions textAlign={TextAlign.CENTER}>
                <Button
                  {...intro.cta}
                  color={ButtonColor.PRIMARY}
                  onClick={scrollToRef(current)}
                />
              </PageSection.Actions>

              <Spacing margin={[SpacingName.BIG, 0, 0]}>
                <Typography.H4
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {intro.description}
                </Typography.H4>
              </Spacing>
            </div>
          </PageSection.Wrapper>
        </PageSection>
      )}

      {!isLanding && <SectionDivider />}

      {/** Vantaggi */}
      <PageSection aria-label="Vantaggi">
        <PageSection.Wrapper>
          <div css={styles.advantagesTitle}>
            {advantages.title}
          </div>

          {advantages.subtitle && (
            <Spacing margin={[SpacingName.SMALLER, SpacingName.SMALL]}>
              <div css={styles.advantagesSubtitle}>
                <Typography.H3
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {advantages.subtitle}
                </Typography.H3>
              </div>
            </Spacing>
          )}
          <PageSection padding={[SpacingName.BIGGER, 0]}>
            <PageSection.Wrapper>
              <FeatureBlock
                title={{
                  tag: 'h3',
                  content: advantages.items.fee.title,
                }}
                subtitle={advantages.items.fee.subtitle}
                description={advantages.items.fee.description}
              >
                <Image
                  src={requireFromStaticAsset(advantages.items.fee.image.url)}
                  alt={advantages.items.fee.image.alt}
                  lazyLoad
                />
              </FeatureBlock>
            </PageSection.Wrapper>
          </PageSection>
          <Stripe
            imageURL={requireFromStaticAsset(advantages.items.marketing.image.url)}
            imageAlt={advantages.items.marketing.image.alt}
          >
            <Typography.H2
              tagName="h3"
              uppercase
              color="brand.primary"
            >
              {advantages.items.marketing.title}
            </Typography.H2>
            {advantages.items.marketing.subtitle && (
              <Spacing margin={[SpacingName.SMALLEST, 0, 0]}>
                <Typography.H2
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {advantages.items.marketing.subtitle}
                </Typography.H2>
              </Spacing>
            )}
            {advantages.items.marketing.description && (
              <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                <Typography.H4
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {advantages.items.marketing.description}
                </Typography.H4>
              </Spacing>
            )}
          </Stripe>
          <PageSection padding={[SpacingName.BIGGER, 0]}>
            <PageSection.Wrapper>
              <FeatureBlock
                title={{
                  tag: 'h3',
                  content: advantages.items.support.title,
                }}
                subtitle={advantages.items.support.subtitle}
                description={advantages.items.support.description}
              >
                <Typography.H2
                  color="brand.primary"
                  fontWeight={FontWeight.LIGHT}
                >
                  {advantages.items.support.image.title}
                </Typography.H2>
                <div css={styles.supportImageWrapper}>
                  <Image
                    src={requireFromStaticAsset(advantages.items.support.image.url)}
                    alt={advantages.items.support.image.alt}
                    lazyLoad
                  />
                </div>
              </FeatureBlock>
            </PageSection.Wrapper>
          </PageSection>
          <div css={styles.divider} />
          <PageSection padding={[SpacingName.BIGGER, 0]}>
            <PageSection.Wrapper>
              <FeatureBlock
                title={{
                  tag: 'h3',
                  content: advantages.items.technology.title,
                }}
                subtitle={advantages.items.technology.subtitle}
                description={advantages.items.technology.description}
                inverted
              >
                <div
                  css={styles.technologyImageTitle}
                  dangerouslySetInnerHTML={{
                    __html: advantages.items.technology.image.title,
                  }}
                />
                <Image
                  src={requireFromStaticAsset(advantages.items.technology.image.url)}
                  alt={advantages.items.technology.image.alt}
                  lazyLoad
                />
              </FeatureBlock>
            </PageSection.Wrapper>
          </PageSection>
          <Stripe
            imageURL={requireFromStaticAsset(advantages.items.career.image.url)}
            imageAlt={advantages.items.career.image.alt}
            direction={StripeDirection.RIGHT}
          >
            <Typography.H2
              tagName="h3"
              uppercase
              color="brand.primary"
            >
              {advantages.items.career.title}
            </Typography.H2>
            {advantages.items.career.subtitle && (
              <Spacing margin={[SpacingName.SMALLEST, 0, 0]}>
                <Typography.H2
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {advantages.items.career.subtitle}
                </Typography.H2>
              </Spacing>
            )}
            {advantages.items.career.description && (
              <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                <Typography.H4
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {advantages.items.career.description}
                </Typography.H4>
              </Spacing>
            )}
          </Stripe>
          <PageSection padding={[SpacingName.BIGGER, 0]}>
            <PageSection.Wrapper>
              <FeatureBlock
                title={{
                  tag: 'h3',
                  content: advantages.items.training.title,
                }}
                subtitle={advantages.items.training.subtitle}
                description={advantages.items.training.description}
              >
                <Typography.H2
                  tagName="h3"
                  uppercase
                  color="brand.primary"
                >
                  {advantages.items.network.title}
                </Typography.H2>
                {advantages.items.network.subtitle && (
                  <Spacing margin={[SpacingName.SMALLEST, 0, 0]}>
                    <Typography.H2
                      tagName="p"
                      fontWeight={FontWeight.LIGHT}
                    >
                      {advantages.items.network.subtitle}
                    </Typography.H2>
                  </Spacing>
                )}
                {advantages.items.network.description && (
                  <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                    <Typography.H4
                      tagName="p"
                      fontWeight={FontWeight.LIGHT}
                    >
                      {advantages.items.network.description}
                    </Typography.H4>
                  </Spacing>
                )}
              </FeatureBlock>
              <PageSection.Actions textAlign={TextAlign.CENTER}>
                <Button
                  {...advantages.cta}
                  color={ButtonColor.PRIMARY}
                  onClick={scrollToRef(current)}
                />
              </PageSection.Actions>
            </PageSection.Wrapper>
          </PageSection>
        </PageSection.Wrapper>
      </PageSection>

      {/** Reputazione */}
      <PageSection
        aria-label="Reputazione"
        padding={0}
      >
        <PageSection.Wrapper>
          <div
            css={styles.reputationBlock}
            $mobileCover={requireResourcesFromStaticAssets(reputationSection.backgroundImage).SMALL}
            $desktopCover={requireResourcesFromStaticAssets(reputationSection.backgroundImage).LARGE}
          >
            <div css={styles.reputationBlockWrapper}>
              <PageSection.Title
                content={reputationSection.title}
                textAlign={TextAlign.CENTER}
                color="neutrals.lightest"
              />

              <PageSection.Description
                text={reputationSection.description}
                size={TypographyName.H4}
                textAlign={TextAlign.CENTER}
                color="neutrals.lightest"
              />

              <Spacing margin={[SpacingName.MEDIUM, 0, 0]}>
                <TrustBoxMini
                  theme="dark"
                  clickable={false}
                />
              </Spacing>
            </div>
          </div>
        </PageSection.Wrapper>
      </PageSection>

      {/** Valori */}
      {!isLanding && (
        <PageSection
          padding={[SpacingName.BIGGER, 0, 0]}
          aria-label="Values"
        >
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {valuesSection.title}
            </PageSection.Title>
            <PageSection.Description
              size={TypographyName.H3}
              textAlign={TextAlign.CENTER}
              text={valuesSection.subtitle}
            />
            <PageSection.Block>
              <HAccordion initialOpenBlockPosition={1}>
                {valuesSection.items.map((item) => (
                  <HAccordion.Block
                    key={item.title}
                    title={item.title}
                  >
                    <div css={styles.valueBlock}>
                      <div css={styles.valueBlockLogo}>
                        <Logo />
                      </div>
                      <div css={styles.valueBlockTitle}>
                        {item.title}
                      </div>
                      <div css={styles.valueBlockDescription}>
                        <Markdown>
                          {item.description}
                        </Markdown>
                      </div>
                    </div>
                  </HAccordion.Block>
                ))}
              </HAccordion>
            </PageSection.Block>
            <PageSection.Actions textAlign={TextAlign.CENTER}>
              <Button
                {...valuesSection.cta}
                color={ButtonColor.PRIMARY}
                onClick={scrollToRef(current)}
              />
            </PageSection.Actions>
          </PageSection.Wrapper>
        </PageSection>
      )}

      {/** Storie */}
      {!isLanding && (
        <PageSection
          padding={[SpacingName.BIGGER, 0]}
          aria-label="Stories"
        >
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {storiesSection.title}
            </PageSection.Title>
            <PageSection.Block>
              <Carousel totalElements={storiesSection.items.length}>
                <div css={styles.carouselFrame}>
                  <div css={styles.carouselInnerFrame}>
                    <Carousel.Items>
                      {storiesSection.items.map((agentStory) => (
                        <Carousel.Item key={agentStory.id}>
                          <AgentStory story={agentStory} />
                        </Carousel.Item>
                      ))}
                    </Carousel.Items>
                  </div>
                </div>
                <Spacing margin={[SpacingName.MEDIUM, 0, 0]}>
                  <Carousel.Navigation />
                </Spacing>
              </Carousel>
            </PageSection.Block>
            <PageSection.Actions textAlign={TextAlign.CENTER}>
              <Button
                {...storiesSection.cta}
                color={ButtonColor.PRIMARY}
                onClick={scrollToRef(current)}
              />
            </PageSection.Actions>
          </PageSection.Wrapper>
        </PageSection>
      )}

      {/** Franchising */}
      {!isLanding && (
        <Stripe
          imageURL={requireFromStaticAsset(franchising.image.url)}
          imageAlt={franchising.image.alt}
        >
          <Typography.H2
            color="brand.primary"
            uppercase
          >
            {franchising.title}
          </Typography.H2>
          <Spacing margin={[SpacingName.SMALL, 0]}>
            <Typography.H4
              tagName="p"
              fontWeight={FontWeight.LIGHT}
            >
              {franchising.description}
            </Typography.H4>
          </Spacing>
          <Button
            {...franchising.cta}
            color={ButtonColor.PRIMARY}
            onClick={onFranchisingActionClick}
            href={!isLanding ? FRANCHISING_URL : undefined}
          />
        </Stripe>
      )}

      {/** Modulo di contatto */}
      <div ref={formRef}>
        <PageSection
          padding={[SpacingName.BIGGER, 0]}
          aria-label="Modulo di contatto"
        >
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {contact.title}
            </PageSection.Title>
          </PageSection.Wrapper>

          <PageSection.Wrapper maxWidth={WrapperSize.M}>
            <PageSection.Block>
              <SaveJobApplication
                jobPositionId={jobPosition.id}
                showProvince
                dataTracking="landing-join-our-agents-form-main-submit"
                submitColor={ButtonColor.PRIMARY}
                onSuccess={goToJobApplicationInfoStep}
              />
            </PageSection.Block>
          </PageSection.Wrapper>
        </PageSection>
      </div>
    </Layout>
  );
};

export const getServerSideProps = withGetServerSideError<JoinOurAgentsPageProps>(async ({ query }) => {
  const isLanding = (query?.isLanding as string)?.toLowerCase() === 'true';

  const [data] = await Promise.all([
    importDynamicData(isLanding ? 'pages/landing-join-our-agents.yml' : 'pages/join-our-agents.yml'),
    importDynamicData('common.yml'),
  ]);

  const graphQLClient = initGraphQLClient();

  const [{ data: { agentStories } }, { data: { jobPosition } }] = await Promise.all([
    graphQLClient.query<{ agentStories: AgentStoryModel[] }>({
      query: AGENT_STORIES,
    }),
    graphQLClient.query<{ jobPosition: JobPosition }>({
      query: JOB_POSITION_QUERY,
      variables: {
        id: data.jobPositionId,
      },
    }),
  ]);

  return {
    props: {
      ...data,
      storiesSection: {
        ...data.storiesSection,
        items: agentStories,
      },
      jobPosition,
      isLanding,
    },
  };
});

export default withError(JoinOurAgentsPage);
